(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const media = window.matchMedia(MOBILE_QUERY);
  const homes = new WeakMap();
  const movedNodes = new Set();
  const generated = new Set();
  const observers = new WeakMap();
  const pageScrollButtons = new WeakSet();

  const remember = (node) => {
    if (!node || homes.has(node)) return;
    homes.set(node, { parent: node.parentNode, next: node.nextSibling });
    movedNodes.add(node);
  };

  const move = (node, parent, before = null) => {
    if (!node || !parent) return;
    remember(node);
    parent.insertBefore(node, before);
  };

  const restoreNode = (node) => {
    const home = homes.get(node);
    if (!home?.parent) return;
    if (home.next && home.next.parentNode === home.parent) home.parent.insertBefore(node, home.next);
    else home.parent.appendChild(node);
    homes.delete(node);
    movedNodes.delete(node);
  };

  const addGenerated = (node) => {
    if (!node) return node;
    node.dataset.opsMobileGenerated = 'true';
    generated.add(node);
    return node;
  };

  const removeGenerated = (scope = document) => {
    [...generated].forEach((node) => {
      if (!node.isConnected || scope === document || scope.contains(node)) {
        node.remove();
        generated.delete(node);
      }
    });
  };

  const isProductPage = () => Boolean(document.querySelector('main[data-template="product"]'));


  const ARTWORK_SOURCE_DB = 'ops-artwork-scratchpad';
  const ARTWORK_SOURCE_STORE = 'uploads';
  const PROJECT_SOURCE_DB = 'ops-sticker-sheet-scratchpad';
  const PROJECT_SOURCE_STORE = 'projects';
  let sourceDialog = null;
  let sourceInput = null;
  let sourceRecords = [];
  let sourceObjectUrls = [];

  const sourceDb = (name, storeName) => new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('Browser storage is not supported on this device.'));
      return;
    }
    const request = indexedDB.open(name, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(storeName)) {
        request.result.createObjectStore(storeName, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Saved artwork could not be opened.'));
  });

  const sourceGetAll = async (name, storeName) => {
    const database = await sourceDb(name, storeName);
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(storeName, 'readonly');
      const request = transaction.objectStore(storeName).getAll();
      transaction.oncomplete = () => resolve(request.result || []);
      transaction.onerror = () => reject(transaction.error || request.error || new Error('Saved artwork could not be read.'));
    });
  };

  const safeArtworkFilename = (name = 'saved-artwork.png', type = '') => {
    const cleaned = String(name || 'saved-artwork')
      .normalize('NFKD')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[_\.]+|[_\.]+$/g, '')
      .slice(0, 160) || 'saved-artwork';
    if (/\.[a-z0-9]+$/i.test(cleaned)) return cleaned;
    const normalisedType = String(type || '').toLowerCase();
    const extension = normalisedType.includes('svg') ? '.svg'
      : normalisedType.includes('webp') ? '.webp'
        : normalisedType.includes('jpeg') ? '.jpg'
          : normalisedType.includes('pdf') ? '.pdf'
            : '.png';
    return `${cleaned}${extension}`;
  };

  const normaliseArtworkType = (name = '', type = '') => {
    const normalised = String(type || '').toLowerCase();
    if (/^(image\/(png|jpeg|webp|svg\+xml)|application\/pdf)$/.test(normalised)) return normalised;
    const lowerName = String(name || '').toLowerCase();
    if (lowerName.endsWith('.svg')) return 'image/svg+xml';
    if (lowerName.endsWith('.webp')) return 'image/webp';
    if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) return 'image/jpeg';
    if (lowerName.endsWith('.pdf')) return 'application/pdf';
    return 'image/png';
  };

  const asFile = (blob, name = 'saved-artwork.png', type = '') => {
    if (!(blob instanceof Blob)) return null;
    const finalType = normaliseArtworkType(name, type || blob.type);
    const finalName = safeArtworkFilename(name || blob.name, finalType);
    return new File([blob], finalName, { type: finalType, lastModified: Date.now() });
  };

  const materialiseArtworkFile = async (file) => {
    if (!(file instanceof Blob)) return null;
    const finalType = normaliseArtworkType(file.name, file.type);
    const finalName = safeArtworkFilename(file.name, finalType);
    // Files restored from IndexedDB can retain a lazy browser-backed body on
    // some mobile browsers. Reading the bytes creates a fresh, upload-safe file
    // before it is handed to FormData or a builder.
    const bytes = await file.arrayBuffer();
    return new File([bytes], finalName, { type: finalType, lastModified: Date.now() });
  };

  async function readArtworkSources() {
    const [artwork, projects] = await Promise.all([
      sourceGetAll(ARTWORK_SOURCE_DB, ARTWORK_SOURCE_STORE).catch(() => []),
      sourceGetAll(PROJECT_SOURCE_DB, PROJECT_SOURCE_STORE).catch(() => []),
    ]);
    const items = [];
    artwork.forEach((record) => {
      if (record.temporaryProofBridge || record.temporaryBuilderBridge || record.temporaryBridge) return;
      const source = record.originalFile instanceof Blob ? record.originalFile : record.file;
      const file = asFile(source, record.name, record.type || source?.type);
      if (!file) return;
      items.push({
        id: `artwork:${record.id}`,
        name: record.name || file.name || 'Saved artwork',
        detail: 'My Artwork',
        file,
        preview: record.thumbnail instanceof Blob ? record.thumbnail : file,
      });
    });
    projects.forEach((project) => {
      const candidates = [];
      (project.assets || []).forEach((asset, index) => candidates.push({
        source: asset.originalFile || asset.file,
        name: asset.originalName || asset.name || `project-artwork-${index + 1}.png`,
        type: asset.type,
        key: asset.id || `asset-${index}`,
      }));
      (project.surfaces || []).forEach((surface, index) => candidates.push({
        source: surface.originalFile,
        name: surface.imageName || `surface-${index + 1}.png`,
        type: surface.originalFile?.type,
        key: `surface-${index}`,
      }));
      (project.originalFiles || []).forEach((source, index) => candidates.push({
        source,
        name: source?.name || `original-${index + 1}.png`,
        type: source?.type,
        key: `original-${index}`,
      }));
      const seen = new Set();
      candidates.forEach((candidate, index) => {
        const source = candidate.source;
        if (!(source instanceof Blob)) return;
        const fingerprint = `${candidate.name}|${source.size}|${source.type}`;
        if (seen.has(fingerprint)) return;
        seen.add(fingerprint);
        const file = asFile(source, candidate.name, candidate.type || source.type);
        if (!file) return;
        items.push({
          id: `project:${project.id}:${candidate.key || index}`,
          name: candidate.name || file.name || 'Project artwork',
          detail: project.name || 'Builder project',
          file,
          preview: file,
        });
      });
    });
    return items;
  }

  async function deliverArtworkFile(input, file) {
    if (!input || !file) return false;
    let freshFile;
    try {
      freshFile = await materialiseArtworkFile(file);
      if (!freshFile) return false;

      // Prefer each tool's own loading API. This avoids relying on DataTransfer
      // for browser-restored files, which can produce malformed multipart
      // uploads on some mobile browsers.
      const builder = input.closest('sticker-sheet-builder');
      if (builder?.handleFiles) {
        await builder.handleFiles([freshFile]);
        return true;
      }
      const proof = input.closest('ops-artwork-proof-lab');
      if (proof?.load) {
        await proof.load(freshFile);
        return true;
      }
      const diecut = input.closest('ops-die-cut-proof-tool');
      if (diecut?.loadFile) {
        await diecut.loadFile(freshFile);
        return true;
      }
      const makerRoot = input.closest('.ops-maker');
      const maker = makerRoot?.opsMakerInstance;
      // Gift Wrap secondary tile images must reach the tile layer input's change
      // handler. Calling the generic maker.loadFile() would replace the tile image.
      if (maker?.loadFile && !input.matches('[data-gw-extra-upload]')) {
        await maker.loadFile(freshFile);
        return true;
      }
      const teeRoot = input.closest('[data-ops-tee-maker]');
      const teeMaker = teeRoot?.opsTeeMaker;
      if (teeMaker?.loadFile) {
        await teeMaker.loadFile(freshFile);
        return true;
      }

      const transfer = new DataTransfer();
      transfer.items.add(freshFile);
      input.files = transfer.files;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    } catch (error) {
      console.warn('The chosen artwork could not be passed to this uploader.', error);
      return false;
    }
  }

  function closeSourceDialog() {
    if (!sourceDialog) return;
    if (sourceDialog.open) sourceDialog.close();
    sourceInput = null;
  }

  function clearSourceUrls() {
    sourceObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    sourceObjectUrls = [];
  }

  function ensureSourceDialog() {
    if (sourceDialog?.isConnected) return sourceDialog;
    sourceDialog = addGenerated(document.createElement('dialog'));
    sourceDialog.className = 'ops-mobile-source-dialog';
    sourceDialog.innerHTML = `
      <div class="ops-mobile-source-dialog__shell">
        <header>
          <div><small>Choose artwork</small><h2>Where is your artwork?</h2></div>
          <button type="button" data-source-close aria-label="Close artwork choices">×</button>
        </header>
        <button type="button" class="ops-mobile-source-device" data-source-device>
          <strong>Upload from this device</strong><span>Photos, files or cloud storage on this device</span>
        </button>
        <section class="ops-mobile-source-saved">
          <div class="ops-mobile-source-heading"><strong>My Artwork</strong><span data-source-count></span></div>
          <div class="ops-mobile-source-grid" data-source-grid><p>Loading saved artwork…</p></div>
        </section>
        <details class="ops-mobile-source-url">
          <summary>Import from an image URL</summary>
          <div>
            <input type="url" inputmode="url" placeholder="https://example.com/artwork.png" data-source-url>
            <button type="button" data-source-import>Import URL</button>
            <small>Direct image links work when the website permits cross-site downloads.</small>
            <p data-source-status aria-live="polite"></p>
          </div>
        </details>
      </div>`;
    document.body.appendChild(sourceDialog);

    sourceDialog.querySelector('[data-source-close]')?.addEventListener('click', closeSourceDialog);
    sourceDialog.addEventListener('click', (event) => { if (event.target === sourceDialog) closeSourceDialog(); });
    sourceDialog.querySelector('[data-source-device]')?.addEventListener('click', () => {
      const input = sourceInput;
      closeSourceDialog();
      if (!input) return;
      input.dataset.opsArtworkSourceDirect = 'true';
      input.click();
    });
    sourceDialog.querySelector('[data-source-grid]')?.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-source-index]');
      if (!button) return;
      const record = sourceRecords[Number(button.dataset.sourceIndex)];
      if (record?.file && sourceInput && await deliverArtworkFile(sourceInput, record.file)) closeSourceDialog();
    });
    sourceDialog.querySelector('[data-source-import]')?.addEventListener('click', async () => {
      const urlInput = sourceDialog.querySelector('[data-source-url]');
      const status = sourceDialog.querySelector('[data-source-status]');
      const url = urlInput?.value.trim();
      if (!url || !sourceInput) return;
      status.textContent = 'Importing image…';
      try {
        const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error(`Image request failed (${response.status}).`);
        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) throw new Error('That URL did not return an image.');
        const pathName = new URL(url, window.location.href).pathname.split('/').pop() || 'imported-artwork';
        const extension = blob.type.split('/')[1]?.replace('svg+xml', 'svg') || 'png';
        const name = /\.[a-z0-9]+$/i.test(pathName) ? pathName : `${pathName}.${extension}`;
        const file = new File([blob], name, { type: blob.type, lastModified: Date.now() });
        if (await deliverArtworkFile(sourceInput, file)) closeSourceDialog();
      } catch (error) {
        status.textContent = `${error.message || 'That image could not be imported.'} Download it to your device and use Upload from this device instead.`;
      }
    });
    return sourceDialog;
  }

  async function openSourceDialog(input) {
    sourceInput = input;
    const dialog = ensureSourceDialog();
    const grid = dialog.querySelector('[data-source-grid]');
    const count = dialog.querySelector('[data-source-count]');
    const status = dialog.querySelector('[data-source-status]');
    if (status) status.textContent = '';
    clearSourceUrls();
    sourceRecords = [];
    grid.innerHTML = '<p>Loading saved artwork…</p>';
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    try {
      sourceRecords = await readArtworkSources();
      count.textContent = sourceRecords.length ? `${sourceRecords.length} saved` : '';
      grid.innerHTML = '';
      if (!sourceRecords.length) {
        grid.innerHTML = '<p class="ops-mobile-source-empty">Nothing saved yet. Upload from your device or save artwork through My Artwork first.</p>';
        return;
      }
      sourceRecords.forEach((record, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.sourceIndex = String(index);
        const url = URL.createObjectURL(record.preview || record.file);
        sourceObjectUrls.push(url);
        const img = document.createElement('img');
        img.src = url;
        img.alt = '';
        const copy = document.createElement('span');
        const strong = document.createElement('strong');
        strong.textContent = record.name;
        const small = document.createElement('small');
        small.textContent = record.detail;
        copy.append(strong, small);
        button.append(img, copy);
        grid.appendChild(button);
      });
    } catch (error) {
      grid.innerHTML = `<p class="ops-mobile-source-empty">${error.message || 'Saved artwork could not be opened.'}</p>`;
    }
  }

  function bindArtworkSourceInput(input) {
    if (!input || input.dataset.opsArtworkSourceReady === 'true') return;
    input.dataset.opsArtworkSourceReady = 'true';
    input.addEventListener('click', (event) => {
      if (input.dataset.opsArtworkSourceDirect === 'true') {
        delete input.dataset.opsArtworkSourceDirect;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      openSourceDialog(input);
    }, true);
  }

  function addSourceLauncher(input, anchor, label = 'Choose artwork') {
    if (!input || !anchor || input.dataset.opsMobileSourceReady === 'true') return;
    input.dataset.opsMobileSourceReady = 'true';
    const button = addGenerated(document.createElement('button'));
    button.type = 'button';
    button.className = 'ops-mobile-source-launch';
    button.innerHTML = `<span aria-hidden="true">＋</span><strong>${label}</strong><small>Device · My Artwork · Image URL</small>`;
    button.addEventListener('click', () => openSourceDialog(input));
    anchor.parentNode.insertBefore(button, anchor);
  }

  function initArtworkSources(scope = document) {
    scope.querySelectorAll('.ops-builder-section [data-artwork-input]').forEach((input) => {
      bindArtworkSourceInput(input);
      if (!media.matches) return;
      addSourceLauncher(input, input.closest('.ops-builder-upload'), 'Add artwork');
      input.closest('.ops-builder-section')?.querySelectorAll('[data-empty-upload], [data-quick-upload]').forEach((trigger) => {
        if (trigger.dataset.opsMobileSourceTrigger === 'true') return;
        trigger.dataset.opsMobileSourceTrigger = 'true';
        trigger.addEventListener('click', (event) => {
          if (!media.matches) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          openSourceDialog(input);
        }, true);
      });
    });
    scope.querySelectorAll('.ops-proof-page [data-file]').forEach((input) => {
      bindArtworkSourceInput(input);
      if (!media.matches) return;
      const anchor = input.closest('.ops-proof-page')?.querySelector('.ops-proof-panel .ops-proof-primary');
      addSourceLauncher(input, anchor, 'Choose artwork');
      input.closest('.ops-proof-page')?.querySelectorAll('[data-pick]').forEach((trigger) => {
        if (trigger.dataset.opsMobileSourceTrigger === 'true') return;
        trigger.dataset.opsMobileSourceTrigger = 'true';
        trigger.addEventListener('click', (event) => {
          if (!media.matches) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          openSourceDialog(input);
        }, true);
      });
    });
    scope.querySelectorAll('.ops-diecut-page [data-file]').forEach((input) => {
      bindArtworkSourceInput(input);
      if (!media.matches) return;
      const anchor = input.closest('.ops-diecut-page')?.querySelector('.ops-diecut-panel [data-pick]');
      addSourceLauncher(input, anchor, 'Choose artwork');
      input.closest('.ops-diecut-page')?.querySelectorAll('[data-pick], [data-drop]').forEach((trigger) => {
        if (trigger.dataset.opsMobileSourceTrigger === 'true') return;
        trigger.dataset.opsMobileSourceTrigger = 'true';
        trigger.addEventListener('click', (event) => {
          if (!media.matches) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          openSourceDialog(input);
        }, true);
      });
    });
    scope.querySelectorAll('.ops-maker [data-upload], [data-ops-artwork-source]').forEach((input) => {
      bindArtworkSourceInput(input);
      if (!media.matches) return;
      const anchor = input.closest('.ops-maker__group')?.querySelector('[data-upload-button]');
      addSourceLauncher(input, anchor, 'Choose artwork');
      if (anchor && anchor.dataset.opsMobileSourceTrigger !== 'true') {
        anchor.dataset.opsMobileSourceTrigger = 'true';
        anchor.addEventListener('click', (event) => {
          if (!media.matches) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          openSourceDialog(input);
        }, true);
      }
    });
    scope.querySelectorAll('[data-ops-tee-maker] [data-tee-upload]').forEach((input) => {
      bindArtworkSourceInput(input);
      if (!media.matches) return;
      const root = input.closest('[data-ops-tee-maker]');
      const anchor = root?.querySelector('[data-tee-upload-button]');
      if (anchor && anchor.dataset.opsMobileSourceTrigger !== 'true') {
        anchor.dataset.opsMobileSourceTrigger = 'true';
        anchor.addEventListener('click', (event) => {
          if (!media.matches) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          openSourceDialog(input);
        }, true);
      }
    });
    scope.querySelectorAll('.custom-upload-block .custom-upload-input').forEach((input) => {
      bindArtworkSourceInput(input);
      if (!media.matches) return;
      const anchor = input.closest('.custom-upload-block')?.querySelector('.custom-upload-actions');
      addSourceLauncher(input, anchor, 'Choose artwork');
    });
  }

  function createTabBar(items, onSelect, className) {
    const nav = addGenerated(document.createElement('nav'));
    nav.className = className;
    nav.setAttribute('aria-label', 'Mobile editor controls');

    items.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = item.label;
      button.dataset.mobileTab = item.key;
      button.setAttribute('aria-pressed', String(index === 0));
      button.addEventListener('click', () => {
        nav.querySelectorAll('[data-mobile-tab]').forEach((candidate) => {
          candidate.setAttribute('aria-pressed', String(candidate === button));
        });
        onSelect(item.key);
      });
      nav.appendChild(button);
    });

    return nav;
  }

  function addEditorLink(parent, href, title, copy = 'Open the full editor for advanced changes') {
    if (!parent || parent.querySelector(':scope > .ops-mobile-editor-link')) return;
    const link = addGenerated(document.createElement('a'));
    link.className = 'ops-mobile-editor-link';
    link.href = href;
    link.innerHTML = `<strong>${title}</strong><small>${copy}</small><span aria-hidden="true">→</span>`;
    parent.appendChild(link);
  }

  function initProofLab(scope = document) {
    scope.querySelectorAll('.ops-proof-page:not([data-ops-mobile-proof])').forEach((root) => {
      root.dataset.opsMobileProof = 'true';
      const workspace = root.querySelector('.ops-proof-workspace');
      const cards = [...root.querySelectorAll('.ops-proof-panel > .ops-proof-card')];
      if (!workspace || cards.length < 3) return;

      const groups = ['artwork', 'size', 'colour', 'more', 'download'];
      cards.forEach((card, index) => {
        card.dataset.opsMobilePanel = groups[index] || 'more';
      });

      const setPanel = (key) => {
        root.dataset.opsMobileActive = key;
        cards.forEach((card) => card.classList.toggle('is-ops-mobile-active', card.dataset.opsMobilePanel === key));
      };

      const nav = createTabBar(
        [
          { key: 'artwork', label: 'Artwork' },
          { key: 'size', label: 'Size' },
          { key: 'colour', label: 'Colour' },
          { key: 'more', label: 'More' },
          { key: 'download', label: 'Download' },
        ],
        setPanel,
        'ops-mobile-editor-tabs ops-mobile-proof-tabs'
      );

      workspace.appendChild(nav);
      setPanel('artwork');
    });
  }

  function initDieCut(scope = document) {
    scope.querySelectorAll('.ops-diecut-page:not([data-ops-mobile-diecut])').forEach((root) => {
      root.dataset.opsMobileDiecut = 'true';
      const stage = root.querySelector('.ops-diecut-stage');
      const app = root.querySelector('.ops-diecut-app');
      const panel = root.querySelector('.ops-diecut-panel');
      const controls = [...root.querySelectorAll('.ops-diecut-panel > .ops-diecut-control')];
      if (!stage || !app || !panel || controls.length < 5) return;

      const wrapper = addGenerated(document.createElement('div'));
      wrapper.className = 'ops-mobile-diecut-workspace';
      app.insertBefore(wrapper, stage);
      move(stage, wrapper);

      controls.forEach((control, index) => {
        control.dataset.opsMobileDiecutControl = ['artwork', 'size', 'shape', 'edge', 'save'][index] || 'artwork';
      });

      const artworkControl = controls[0];
      artworkControl.querySelector('[data-pick]')?.setAttribute('data-ops-mobile-role', 'pick');
      artworkControl.querySelector('[data-remove-background]')?.closest('label')?.setAttribute('data-ops-mobile-role', 'remove');
      artworkControl.querySelector('[data-tolerance-wrap]')?.setAttribute('data-ops-mobile-role', 'tolerance');
      controls[3].querySelector('[data-border-toggle]')?.closest('label')?.setAttribute('data-ops-mobile-role', 'border-toggle');
      controls[3].querySelector('[data-border-width-wrap]')?.setAttribute('data-ops-mobile-role', 'border-width');
      controls[3].querySelector('[data-smoothing-wrap]')?.setAttribute('data-ops-mobile-role', 'advanced');
      controls[3].querySelector('[data-swatches]')?.closest('.ops-diecut-subcontrol')?.setAttribute('data-ops-mobile-role', 'advanced');
      controls[3].querySelector('[data-cut-toggle]')?.closest('label')?.setAttribute('data-ops-mobile-role', 'cut-toggle');

      const progress = addGenerated(document.createElement('div'));
      progress.className = 'ops-mobile-diecut-progress';
      progress.innerHTML = '<span></span><strong></strong><i></i>';
      wrapper.appendChild(progress);

      const nav = addGenerated(document.createElement('div'));
      nav.className = 'ops-mobile-diecut-nav';
      nav.innerHTML = '<button type="button" data-diecut-back>Back</button><button type="button" data-diecut-next>Next</button>';
      panel.appendChild(nav);

      const steps = [
        { title: 'Upload artwork', control: 'artwork' },
        { title: 'Prepare artwork', control: 'artwork' },
        { title: 'Finished size', control: 'size' },
        { title: 'Sticker shape', control: 'shape' },
        { title: 'Border', control: 'edge' },
        { title: 'Cut line', control: 'edge' },
        { title: 'Save or order', control: 'save' },
      ];
      let current = 1;

      const render = (scroll = false) => {
        const step = steps[current - 1];
        root.dataset.opsMobileDiecutStep = String(current);
        controls.forEach((control) => control.classList.toggle('is-ops-mobile-active', control.dataset.opsMobileDiecutControl === step.control));
        progress.querySelector('span').textContent = `Step ${current} of ${steps.length}`;
        progress.querySelector('strong').textContent = step.title;
        progress.querySelector('i').style.setProperty('--ops-mobile-progress', `${(current / steps.length) * 100}%`);
        nav.querySelector('[data-diecut-back]').hidden = current === 1;
        const next = nav.querySelector('[data-diecut-next]');
        next.hidden = current === steps.length;
        next.textContent = current === steps.length - 1 ? 'Next: save' : 'Next';
        if (scroll) {
          const top = Math.max(0, wrapper.getBoundingClientRect().top + window.scrollY - 8);
          window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      };
      nav.querySelector('[data-diecut-back]').addEventListener('click', () => { current = Math.max(1, current - 1); render(true); });
      nav.querySelector('[data-diecut-next]').addEventListener('click', () => { current = Math.min(steps.length, current + 1); render(true); });
      render();
    });
  }

  function initProductUploads(scope = document) {
    if (!isProductPage()) return;
    scope.querySelectorAll('.custom-upload-block:not([data-ops-mobile-upload])').forEach((block) => {
      block.dataset.opsMobileUpload = 'true';
      const type = block.dataset.previewType || 'generic';
      const productInfo = block.closest('product-info');
      const diecutUrl = productInfo?.dataset.opsDiecutMakerUrl || '/pages/die-cut-sticker-maker';
      const stickerSheetUrl = productInfo?.dataset.opsStickerSheetMakerUrl || '/pages/sticker-sheet-maker';
      const proofLabUrl = productInfo?.dataset.opsProofLabUrl || '/pages/artwork-proof-lab';
      const map = {
        'die-cut': [diecutUrl, 'Open the Die-Cut Sticker Maker'],
        'sticker-sheet': [stickerSheetUrl, 'Open the Sticker Sheet Maker'],
        flag: [proofLabUrl, 'Open the Artwork Proof Lab'],
        mug: [proofLabUrl, 'Open the Artwork Proof Lab'],
        generic: [proofLabUrl, 'Open the Artwork Proof Lab'],
      };
      const [href, title] = map[type] || map.generic;
      addEditorLink(block, href, title, 'Something not right? Use the full artwork editor');
    });
  }


  /* V20.3.25 — one persistent artwork command bar.
   * This floating shelf is deliberately EDITOR-ONLY: Artwork, Layer and Preview.
   * It never owns price, cart, download or order actions. Those stay in each
   * maker/product's normal workflow so the shelf only appears where artwork tools
   * are genuinely useful.
   */
  const surfaceButtonActive = (button) => button && (
    button.getAttribute('aria-selected') === 'true' ||
    button.getAttribute('aria-pressed') === 'true' ||
    button.classList.contains('is-active') ||
    button.classList.contains('active')
  );

  function addCommandBarTools({ root, bar, uploadInput = null, surfaceButtonsGetter = null, canvasGetter = null, imageGetter = null, label = 'Artwork' }) {
    if (!root || !bar) return false;
    if (bar.dataset.opsCommandToolsReady === 'true') return true;

    const controls = document.createElement('div');
    controls.className = 'ops-maker-command-bar__controls';
    controls.dataset.opsCommandTools = 'true';
    controls.innerHTML = `
      <button type="button" class="ops-maker-command-bar__tool is-artwork" data-command-artwork>
        <span class="ops-maker-command-bar__tool-icon" aria-hidden="true">＋</span><small>Artwork</small>
      </button>
      <div class="ops-maker-command-bar__tool-wrap is-layer" data-command-layer-wrap>
        <button type="button" class="ops-maker-command-bar__tool" data-command-layer aria-expanded="false">
          <strong data-command-layer-label>Front</strong><small>Layer</small>
        </button>
        <div class="ops-maker-command-bar__layer-popover" data-command-layer-popover hidden>
          <div class="ops-maker-command-bar__popover-head"><strong>Choose layer</strong><button type="button" data-command-layer-close aria-label="Close layer menu">×</button></div>
          <div class="ops-maker-command-bar__layer-list" data-command-layer-list></div>
        </div>
      </div>
      <div class="ops-maker-command-bar__tool-wrap is-preview" data-command-preview-wrap>
        <button type="button" class="ops-maker-command-bar__tool is-preview" data-command-preview aria-expanded="false">
          <span class="ops-maker-command-bar__thumb" aria-hidden="true"><img alt=""></span><small>Preview</small>
        </button>
        <div class="ops-maker-command-bar__preview-popover" data-command-preview-popover hidden>
          <div class="ops-maker-command-bar__popover-head"><div><small data-command-preview-layer>${label}</small><strong>Your design</strong></div><button type="button" data-command-preview-close aria-label="Close preview">×</button></div>
          <div class="ops-maker-command-bar__preview-frame"><img data-command-preview-image alt="Current design preview"></div>
        </div>
      </div>`;
    bar.insertBefore(controls, bar.firstChild);

    const artworkButton = controls.querySelector('[data-command-artwork]');
    const layerWrap = controls.querySelector('[data-command-layer-wrap]');
    const layerButton = controls.querySelector('[data-command-layer]');
    const layerLabel = controls.querySelector('[data-command-layer-label]');
    const layerPopover = controls.querySelector('[data-command-layer-popover]');
    const layerList = controls.querySelector('[data-command-layer-list]');
    const layerClose = controls.querySelector('[data-command-layer-close]');
    const previewWrap = controls.querySelector('[data-command-preview-wrap]');
    const previewButton = controls.querySelector('[data-command-preview]');
    const previewPopover = controls.querySelector('[data-command-preview-popover]');
    const previewClose = controls.querySelector('[data-command-preview-close]');
    const previewLayer = controls.querySelector('[data-command-preview-layer]');
    const previewImage = controls.querySelector('[data-command-preview-image]');
    const thumbImage = controls.querySelector('.ops-maker-command-bar__thumb img');

    if (!uploadInput) artworkButton.hidden = true;
    artworkButton?.addEventListener('click', () => uploadInput && openSourceDialog(uploadInput));

    const surfaceButtons = () => [...(surfaceButtonsGetter?.() || [])].filter((button) => !button.hidden && !button.disabled && button.textContent.trim());
    const activeSurface = () => surfaceButtons().find(surfaceButtonActive) || surfaceButtons()[0] || null;

    const closeLayer = () => {
      if (!layerPopover || !layerButton) return;
      layerPopover.hidden = true;
      layerButton.setAttribute('aria-expanded', 'false');
      layerWrap?.classList.remove('is-open');
    };
    const closePreview = () => {
      if (!previewPopover || !previewButton) return;
      previewPopover.hidden = true;
      previewButton.setAttribute('aria-expanded', 'false');
      previewWrap?.classList.remove('is-open');
    };

    const renderLayers = () => {
      const buttons = surfaceButtons();
      if (layerWrap) layerWrap.hidden = buttons.length < 2;
      if (!layerList) return;
      const active = activeSurface();
      const activeText = active?.textContent.trim() || 'Front';
      if (layerLabel) layerLabel.textContent = activeText;
      if (previewLayer) previewLayer.textContent = activeText;
      const signature = buttons.map((button) => button.textContent.trim()).join('|');
      if (layerList.dataset.signature !== signature) {
        layerList.dataset.signature = signature;
        layerList.replaceChildren();
        buttons.forEach((button) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'ops-maker-command-bar__layer-chip';
          chip.textContent = button.textContent.trim();
          chip.addEventListener('click', () => {
            button.click();
            closeLayer();
            scheduleSync();
          });
          layerList.appendChild(chip);
        });
      }
      layerList.querySelectorAll('button').forEach((chip) => chip.classList.toggle('is-active', chip.textContent.trim() === activeText));
    };

    const snapshot = () => {
      let src = '';
      const canvas = canvasGetter?.();
      if (canvas?.toDataURL && !canvas.hidden) {
        try { src = canvas.toDataURL('image/png'); } catch (_) {}
      }
      if (!src) {
        const image = imageGetter?.();
        if (image?.src) src = image.src;
      }
      if (src) {
        if (previewImage) previewImage.src = src;
        if (thumbImage) thumbImage.src = src;
        bar.classList.add('has-preview');
      }
    };

    const syncAll = () => {
      renderLayers();
      const hasPreviewSource = Boolean(canvasGetter?.() || imageGetter?.());
      if (previewWrap) previewWrap.hidden = !hasPreviewSource;
      if (!hasPreviewSource) closePreview();
      snapshot();

      const hasVisibleTool = [artworkButton, layerWrap, previewWrap].some((node) => node && !node.hidden);
      bar.hidden = !hasVisibleTool || bar.dataset.opsOwnerVisible === 'false';
    };
    const scheduleSync = () => {
      requestAnimationFrame(syncAll);
      window.setTimeout(syncAll, 80);
      window.setTimeout(snapshot, 250);
    };

    layerButton?.addEventListener('click', () => {
      const open = layerPopover?.hidden !== false;
      closePreview();
      if (!layerPopover) return;
      layerPopover.hidden = !open;
      layerButton.setAttribute('aria-expanded', String(open));
      layerWrap?.classList.toggle('is-open', open);
      if (open) renderLayers();
    });
    layerClose?.addEventListener('click', closeLayer);
    previewButton?.addEventListener('click', () => {
      const open = previewPopover?.hidden !== false;
      closeLayer();
      if (!previewPopover) return;
      previewPopover.hidden = !open;
      previewButton.setAttribute('aria-expanded', String(open));
      previewWrap?.classList.toggle('is-open', open);
      if (open) scheduleSync();
    });
    previewClose?.addEventListener('click', closePreview);

    document.addEventListener('pointerdown', (event) => {
      if (!bar.isConnected || bar.contains(event.target)) return;
      closeLayer();
      closePreview();
    });
    ['input', 'change', 'click', 'pointerup'].forEach((eventName) => root.addEventListener(eventName, scheduleSync));
    const tabs = surfaceButtonsGetter?.();
    if (tabs?.length) tabs.forEach((button) => button.addEventListener('click', scheduleSync));
    const canvas = canvasGetter?.();
    if (canvas) new MutationObserver(scheduleSync).observe(canvas, { attributes: true });

    bar.dataset.opsCommandToolsReady = 'true';
    scheduleSync();
    return true;
  }

  function enhanceExistingCommandBar({ root, bar, uploadInput, surfaceButtonsGetter, canvasGetter, imageGetter = null }) {
    if (!root || !bar) return false;
    bar.classList.add('ops-maker-command-bar', 'ops-maker-command-bar--tools-only');
    root.classList.add('ops-maker-command-bar-host', 'ops-maker-command-bar-tools-only-host');
    addCommandBarTools({ root, bar, uploadInput, surfaceButtonsGetter, canvasGetter, imageGetter });
    return true;
  }

  function cleanupOrphanCommandBars() {
    document.querySelectorAll('body > .ops-maker-command-bar--generated[data-ops-universal-command-bar]').forEach((bar) => {
      const owner = bar._opsCommandOwnerRoot;
      if (owner && !owner.isConnected) { bar._opsOwnerObserver?.disconnect?.(); bar.remove(); }
    });
  }

  function findCommandBar(root, key) {
    return [...document.querySelectorAll('body > .ops-maker-command-bar--generated[data-ops-universal-command-bar]')]
      .find((bar) => bar._opsCommandOwnerRoot === root && bar.dataset.opsUniversalCommandBar === key) || null;
  }

  function createUniversalCommandBar({ root, key, uploadInput, surfaceButtonsGetter = null, canvasGetter = null, imageGetter = null }) {
    if (!root) return null;

    const hasUpload = Boolean(uploadInput);
    const hasLayers = [...(surfaceButtonsGetter?.() || [])].filter((button) => !button.hidden && !button.disabled && button.textContent.trim()).length >= 2;
    const hasPreview = Boolean(canvasGetter?.() || imageGetter?.());
    if (!hasUpload && !hasLayers && !hasPreview) return null;

    cleanupOrphanCommandBars();
    const existing = findCommandBar(root, key);
    if (existing) return existing;

    root.classList.add('ops-maker-command-bar-host', 'ops-maker-command-bar-tools-only-host');
    const bar = document.createElement('div');
    bar.className = 'ops-maker-command-bar ops-maker-command-bar--generated ops-maker-command-bar--tools-only';
    bar.dataset.opsUniversalCommandBar = key;
    bar._opsCommandOwnerRoot = root;

    // Mount generated bars directly under <body>. A fixed element can otherwise be
    // trapped by transformed/filtered product containers and appear stuck part-way
    // down a product description instead of following the viewport.
    document.body.appendChild(bar);

    bar.dataset.opsOwnerVisible = 'true';
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = Boolean(entries[0]?.isIntersecting);
        bar.dataset.opsOwnerVisible = visible ? 'true' : 'false';
        if (!visible) bar.hidden = true;
        else requestAnimationFrame(() => {
          const hasVisibleTool = [...bar.querySelectorAll('[data-command-artwork],[data-command-layer-wrap],[data-command-preview-wrap]')].some(node => node && !node.hidden);
          bar.hidden = !hasVisibleTool;
        });
      }, { root: null, threshold: 0 });
      observer.observe(root);
      bar._opsOwnerObserver = observer;
    }

    if (!addCommandBarTools({ root, bar, uploadInput, surfaceButtonsGetter, canvasGetter, imageGetter })) {
      bar.remove();
      return null;
    }
    return bar;
  }

  function initPersistentMakerBars(scope = document) {
    scope.querySelectorAll('[data-ops-maker]').forEach((root) => {
      const sourceBar = root.querySelector('.ops-maker__mobile-order');
      const bar = createUniversalCommandBar({
        root, key: 'shared-maker',
        uploadInput: root.querySelector('[data-upload]'),
        surfaceButtonsGetter: () => root.querySelectorAll('[data-surface]'),
        canvasGetter: () => root.querySelector('.ops-maker__canvas'),
        imageGetter: () => root.querySelector('.ops-maker__stage img, .ops-maker__preview img'),
      });
      if (bar) {
        sourceBar?.classList.add('ops-command-bar-source');
        root.classList.add('ops-mobile-command-bar-ready');
      }
    });

    scope.querySelectorAll('[data-ops-tee-maker]').forEach((root) => {
      const sourceBar = root.querySelector('.ops-tee__mobile-bar');
      const bar = createUniversalCommandBar({
        root, key: 'tshirt-maker',
        uploadInput: root.querySelector('[data-tee-upload]'),
        surfaceButtonsGetter: () => root.querySelectorAll('[data-tee-surface]'),
        canvasGetter: () => root.querySelector('[data-tee-canvas]'),
        imageGetter: () => root.querySelector('.ops-tee__preview img'),
      });
      if (bar) sourceBar?.classList.add('ops-command-bar-source');
    });
  }

  function initUniversalCommandBars(scope = document) {
    cleanupOrphanCommandBars();

    scope.querySelectorAll('.ops-diecut-page').forEach((root) => {
      createUniversalCommandBar({
        root, key: 'diecut',
        uploadInput: root.querySelector('[data-file]'),
        canvasGetter: () => root.querySelector('[data-preview]'),
        imageGetter: () => root.querySelector('[data-preview-image]'),
      });
    });

    scope.querySelectorAll('.ops-builder-section').forEach((root) => {
      if (root.classList.contains('ops-builder-section--compact-editor')) return;
      createUniversalCommandBar({
        root, key: 'sticker-sheet',
        uploadInput: root.querySelector('[data-artwork-input]'),
        canvasGetter: () => root.querySelector('[data-builder-canvas]'),
        imageGetter: () => root.querySelector('[data-builder-preview-image]'),
      });
    });

    // Artwork Proof Lab is an editor rather than a Shopify product. It gets only
    // the same Artwork / Preview shelf; its download controls remain in the lab UI.
    scope.querySelectorAll('.ops-proof-page').forEach((root) => {
      createUniversalCommandBar({
        root, key: 'proof-lab',
        uploadInput: root.querySelector('[data-file]'),
        canvasGetter: () => root.querySelector('[data-canvas]'),
        imageGetter: () => root.querySelector('[data-preview-image]'),
      });
    });

    if (!isProductPage()) return;
    if (document.querySelector('[data-ops-maker], [data-ops-tee-maker], .ops-builder-section, .ops-diecut-page')) return;

    // A product page may contain more than one product-form instance. Build one
    // viewport bar for the primary custom-upload form instead of stacking duplicates.
    const forms = [...scope.querySelectorAll('product-form')].filter((form) => (
      form.querySelector('.custom-upload-block .custom-upload-input')
    ));
    const form = forms.find((candidate) => candidate.closest('product-info')) || forms[0];
    if (!form) return;
    const uploadInput = form.querySelector('.custom-upload-block .custom-upload-input');
    createUniversalCommandBar({
      root: form, key: 'product-upload', uploadInput,
      surfaceButtonsGetter: () => form.querySelectorAll('.flag-maker__side-tab'),
      canvasGetter: () => form.querySelector('.flag-preview__canvas, .sticker-sheet-preview__canvas'),
      imageGetter: () => form.querySelector('.artwork-preview__image'),
    });
  }

  function cleanupProductMakerEnhancement(root) {
    if (!root) return;
    delete root.dataset.opsMobileMaker;
    delete root.dataset.opsMobileLayout;
    delete root.dataset.opsMobileMakerBuilding;
    root.classList.remove('ops-mobile-maker-page', 'ops-mobile-maker-stacked');
    root.querySelector('.ops-maker__group[data-maker-panel="artwork"]')?.classList.remove('ops-mobile-maker-upload-panel');
    root.querySelector('.ops-maker__stage-wrap')?.classList.remove('ops-mobile-maker-live-preview');
    root.querySelector('.ops-maker__tabs')?.classList.remove('ops-mobile-maker-surface-tabs');
    root.querySelector('[data-maker-panel="text"]')?.classList.remove('ops-mobile-maker-text-panel');
    root.querySelector('[data-maker-panel="canvas"]')?.classList.remove('ops-mobile-maker-guides-panel');
    root.querySelector('.ops-maker__order')?.classList.remove('ops-mobile-maker-final-review');
    root.querySelector('.ops-maker__order-actions')?.classList.remove('ops-mobile-maker-review-actions');
    root.querySelector('.ops-maker__mobile-order')?.classList.remove('ops-mobile-maker-persistent-order', 'ops-mobile-maker-fixed-order-disabled');
    root.querySelectorAll('.ops-mobile-hide').forEach((node) => node.classList.remove('ops-mobile-hide'));
  }

  function initProductMaker(scope = document) {
    scope.querySelectorAll('[data-ops-maker]').forEach((root) => {
      if (root.dataset.opsMobileMaker === 'true' || root.dataset.opsMobileMakerBuilding === 'true') return;

      // Product Maker initialises in a separate deferred asset. Do not hide or
      // rearrange its working controls until that real maker instance exists.
      if (!root.opsMakerInstance) {
        const attempts = Number(root.dataset.opsMobileMakerAttempts || 0);
        if (attempts < 20) {
          root.dataset.opsMobileMakerAttempts = String(attempts + 1);
          window.setTimeout(() => initProductMaker(root.parentElement || document), 75);
        }
        return;
      }
      delete root.dataset.opsMobileMakerAttempts;
      root.dataset.opsMobileMakerBuilding = 'true';

      const movedBefore = new Set(movedNodes);
      const generatedBefore = new Set(generated);

      try {
        const panel = root.querySelector('.ops-maker__panel');
        const stageWrap = root.querySelector('.ops-maker__stage-wrap');
        const orderPanel = root.querySelector('.ops-maker__order');
        const mobileOrder = root.querySelector('.ops-maker__mobile-order');
        const artworkGroup = panel?.querySelector(':scope > [data-maker-panel="artwork"]');
        const textGroup = panel?.querySelector(':scope > [data-maker-panel="text"]');
        const canvasGroup = panel?.querySelector(':scope > [data-maker-panel="canvas"]');
        const surfaceTabs = panel?.querySelector(':scope > .ops-maker__tabs');
        if (!panel || !stageWrap || !orderPanel || !artworkGroup) throw new Error('Shared maker mobile layout is missing a required panel.');

        // Build first. The CSS activation attributes are deliberately applied at
        // the END so an exception can never hide the original working controls.
        artworkGroup.classList.add('ops-mobile-maker-upload-panel');
        artworkGroup.querySelector('.ops-maker__url-row')?.classList.add('ops-mobile-hide');
        const uploadHeading = artworkGroup.querySelector('h3');
        if (uploadHeading) uploadHeading.textContent = 'Upload your artwork';

        const scaleInput = artworkGroup.querySelector('[data-scale]');
        const rotationInput = artworkGroup.querySelector('[data-rotation]');
        const scaleField = scaleInput?.closest('.ops-maker__field');
        const rotationField = rotationInput?.closest('.ops-maker__field');
        const centreButton = artworkGroup.querySelector('[data-centre]');
        const prepare = artworkGroup.querySelector('.ops-maker__prepare');

        move(stageWrap, panel, artworkGroup.nextSibling);
        stageWrap.classList.add('ops-mobile-maker-live-preview');

        if (surfaceTabs) {
          surfaceTabs.classList.add('ops-mobile-maker-surface-tabs');
          move(surfaceTabs, panel, stageWrap.nextSibling);
        }

        const adjustPanel = addGenerated(document.createElement('section'));
        adjustPanel.className = 'ops-maker__group ops-mobile-maker-adjust-panel';
        adjustPanel.innerHTML = `
          <div class="ops-mobile-maker-section-heading"><h3>Size, rotate & position</h3><small>Changes appear in the preview above.</small></div>
          <div class="ops-mobile-maker-adjust-fields"></div>
          <div class="ops-maker__actions ops-mobile-maker-adjust-actions"></div>`;
        const adjustFields = adjustPanel.querySelector('.ops-mobile-maker-adjust-fields');
        const adjustActions = adjustPanel.querySelector('.ops-mobile-maker-adjust-actions');
        if (scaleField) move(scaleField, adjustFields);
        if (rotationField) move(rotationField, adjustFields);
        if (centreButton) move(centreButton, adjustActions);
        panel.insertBefore(adjustPanel, surfaceTabs?.nextSibling || stageWrap.nextSibling);

        const makeAccordion = (title, content, className = '') => {
          if (!content) return null;
          const details = addGenerated(document.createElement('details'));
          details.className = `ops-mobile-maker-accordion ${className}`.trim();
          details.innerHTML = `<summary><span>${title}</span><b aria-hidden="true">＋</b></summary><div class="ops-mobile-maker-accordion__body"></div>`;
          move(content, details.querySelector('.ops-mobile-maker-accordion__body'));
          details.addEventListener('toggle', () => {
            const icon = details.querySelector('summary b');
            if (icon) icon.textContent = details.open ? '−' : '＋';
          });
          return details;
        };
        let insertAfter = adjustPanel;
        const insertAccordion = (details) => {
          if (!details) return;
          insertAfter.insertAdjacentElement('afterend', details);
          insertAfter = details;
        };
        if (prepare) insertAccordion(makeAccordion('Prepare artwork', prepare, 'ops-mobile-maker-prepare-accordion'));
        if (textGroup) {
          textGroup.classList.add('ops-mobile-maker-text-panel');
          insertAccordion(makeAccordion('Add text', textGroup, 'ops-mobile-maker-text-accordion'));
        }
        if (canvasGroup) {
          canvasGroup.classList.add('ops-mobile-maker-guides-panel');
          insertAccordion(makeAccordion('Guides & safe area', canvasGroup, 'ops-mobile-maker-guides-accordion'));
        }

        const leafletSizeControl = panel.querySelector(':scope > .ops-maker__field [data-size]');
        const leafletSizeField = leafletSizeControl?.closest('.ops-maker__field');
        const orderOptions = addGenerated(document.createElement('details'));
        orderOptions.className = 'ops-mobile-maker-accordion ops-mobile-maker-order-options';
        orderOptions.innerHTML = '<summary><span>Product options & quantity</span><b aria-hidden="true">＋</b></summary><div class="ops-mobile-maker-accordion__body"></div>';
        const orderBody = orderOptions.querySelector('.ops-mobile-maker-accordion__body');
        const summary = orderPanel.querySelector('[data-summary]');
        const variantOptions = orderPanel.querySelector('[data-variant-options]');
        const quantityField = orderPanel.querySelector('[data-quantity]')?.closest('.ops-maker__field');
        if (leafletSizeField) move(leafletSizeField, orderBody);
        if (variantOptions) move(variantOptions, orderBody);
        if (quantityField) move(quantityField, orderBody);
        if (summary) {
          summary.classList.add('ops-mobile-maker-order-summary');
          move(summary, orderBody);
        }
        orderOptions.addEventListener('toggle', () => {
          const icon = orderOptions.querySelector('summary b');
          if (icon) icon.textContent = orderOptions.open ? '−' : '＋';
        });
        insertAccordion(orderOptions);

        orderPanel.classList.add('ops-mobile-maker-final-review');
        const orderTitle = [...orderPanel.children].find((node) => node.tagName === 'H3' && !node.hasAttribute('data-review-heading'));
        if (orderTitle) orderTitle.classList.add('ops-mobile-hide');
        orderPanel.querySelector('[data-price]')?.classList.add('ops-mobile-hide');
        orderPanel.querySelector('[data-add]')?.classList.add('ops-mobile-hide');
        const orderActions = orderPanel.querySelector('.ops-maker__order-actions');
        if (orderActions) orderActions.classList.add('ops-mobile-maker-review-actions');
        move(orderPanel, panel, insertAfter.nextSibling);

        if (mobileOrder) {
          mobileOrder.classList.remove('ops-mobile-maker-fixed-order-disabled');
          mobileOrder.classList.add('ops-mobile-maker-persistent-order');
          const mobileAdd = mobileOrder.querySelector('[data-mobile-add]');
          if (mobileAdd) mobileAdd.textContent = 'Add to cart';
          mobileOrder.querySelector('[data-mobile-save]')?.classList.add('ops-mobile-hide');
        }

        // Only NOW activate the mobile CSS. If any line above throws, the catch
        // restores moved nodes and the customer keeps the ordinary working maker.
        root.dataset.opsMobileMaker = 'true';
        root.dataset.opsMobileLayout = 'stacked';
        root.classList.add('ops-mobile-maker-page', 'ops-mobile-maker-stacked');
        delete root.dataset.opsMobileMakerBuilding;

        const refreshReview = () => root.opsMakerInstance?.scheduleReview?.();
        root.addEventListener('input', refreshReview);
        root.addEventListener('change', refreshReview);
        root.querySelectorAll('[data-surface]').forEach((button) => button.addEventListener('click', refreshReview));
        requestAnimationFrame(refreshReview);
      } catch (error) {
        // Restore only nodes/generators introduced by THIS attempt.
        [...movedNodes].filter((node) => !movedBefore.has(node)).reverse().forEach(restoreNode);
        [...generated].filter((node) => !generatedBefore.has(node)).forEach((node) => {
          node.remove();
          generated.delete(node);
        });
        cleanupProductMakerEnhancement(root);
        root.dataset.opsMobileMakerFailed = 'true';
        console.error('OPS shared maker mobile enhancement failed; original controls were restored.', error);
      }
    });
  }

  const builderStepNames = {
    1: 'Upload artwork',
    2: 'Prepare artwork',
    3: 'Size & shape',
    4: 'Sheet spacing',
    5: 'Background & guides',
    6: 'Review & order',
  };

  const setMobileText = (node, text) => {
    if (!node) return;
    if (!node.dataset.opsMobileOriginalText) node.dataset.opsMobileOriginalText = node.textContent;
    node.textContent = text;
  };

  function activateBuilderStep(root, step) {
    const targetStep = Math.max(1, Math.min(5, Number(step) || 1));
    const builder = root.querySelector('sticker-sheet-builder');

    // The persistent five-step footer/tabs were removed in V17.1. The mobile
    // eight-step wizard must therefore drive the builder through its stable
    // component API rather than depending on optional presentation controls.
    if (builder && typeof builder.setStep === 'function') {
      builder.setStep(targetStep, false);
      return Number(builder.dataset.currentStep || 0) === targetStep;
    }

    // Backward-compatible fallback for older themes that still render step tabs.
    const legacyTab = root.querySelector(`[data-step-tab="${targetStep}"]`);
    if (legacyTab) {
      legacyTab.click();
      return true;
    }

    // If the custom element has not upgraded yet, complete the handoff as soon
    // as its class is registered instead of silently leaving panel 1 visible.
    if (builder && window.customElements?.whenDefined) {
      window.customElements.whenDefined('sticker-sheet-builder').then(() => {
        if (typeof builder.setStep === 'function') builder.setStep(targetStep, false);
      });
    }
    return false;
  }

  function createBuilderProgress(root, workspace, productMode) {
    const progress = addGenerated(document.createElement('div'));
    progress.className = productMode ? 'ops-mobile-builder-progress is-quick' : 'ops-mobile-builder-progress';
    progress.innerHTML = `
      <span class="ops-mobile-builder-progress__count"></span>
      <strong class="ops-mobile-builder-progress__title"></strong>
      <span class="ops-mobile-builder-progress__dots" aria-hidden="true"></span>
    `;
    workspace.appendChild(progress);

    const builder = root.querySelector('sticker-sheet-builder');
    const count = progress.querySelector('.ops-mobile-builder-progress__count');
    const title = progress.querySelector('.ops-mobile-builder-progress__title');
    const dots = progress.querySelector('.ops-mobile-builder-progress__dots');

    const update = () => {
      const actual = Number(builder?.dataset.currentStep || 1);
      const displayStep = productMode ? (actual === 5 ? 2 : 1) : actual;
      const total = productMode ? 2 : 5;
      count.textContent = `Step ${displayStep} of ${total}`;
      title.textContent = productMode ? (actual === 5 ? 'Review & order' : 'Upload & preview') : (builderStepNames[actual] || 'Artwork');
      dots.innerHTML = Array.from({ length: total }, (_, index) => `<i class="${index + 1 <= displayStep ? 'is-active' : ''}"></i>`).join('');
      root.dataset.opsMobileBuilderStep = String(actual);
    };

    update();
    if (builder) {
      const observer = new MutationObserver(update);
      observer.observe(builder, { attributes: true, attributeFilter: ['data-current-step'] });
      observers.set(root, observer);
    }
    return progress;
  }

  function createQuickBuilderActions(root) {
    const stepOne = root.querySelector('[data-step-panel="1"]');
    const stepFive = root.querySelector('[data-step-panel="5"]');
    if (!stepOne || !stepFive) return;

    const next = addGenerated(document.createElement('button'));
    next.type = 'button';
    next.className = 'button button--full-width ops-mobile-quick-next';
    next.textContent = 'Next: review & order';
    next.addEventListener('click', () => activateBuilderStep(root, 5));
    stepOne.appendChild(next);

    const back = addGenerated(document.createElement('button'));
    back.type = 'button';
    back.className = 'button button--secondary button--full-width ops-mobile-quick-back';
    back.textContent = 'Back to artwork';
    back.addEventListener('click', () => activateBuilderStep(root, 1));
    stepFive.appendChild(back);
  }

  function simplifyFullBuilder(root, workspace, controls) {
    const stepOne = root.querySelector('[data-step-panel="1"]');
    const stepTwo = root.querySelector('[data-step-panel="2"]');
    const stepThree = root.querySelector('[data-step-panel="3"]');
    const stepFour = root.querySelector('[data-step-panel="4"]');
    const stepFive = root.querySelector('[data-step-panel="5"]');
    if (!stepOne || !stepTwo || !stepThree || !stepFour || !stepFive) return;

    const stepOneTitle = stepOne.querySelector('.ops-builder-card__heading-row h2');
    const prepDetails = stepOne.querySelector('.ops-builder-artwork-prep details');
    if (prepDetails) prepDetails.open = true;

    const progress = addGenerated(document.createElement('div'));
    progress.className = 'ops-mobile-builder-progress';
    progress.innerHTML = '<span class="ops-mobile-builder-progress__count"></span><strong class="ops-mobile-builder-progress__title"></strong><span class="ops-mobile-builder-progress__dots" aria-hidden="true"></span>';
    workspace.appendChild(progress);

    const nav = addGenerated(document.createElement('div'));
    nav.className = 'ops-mobile-builder-wizard-nav';
    nav.innerHTML = '<button type="button" class="button button--secondary" data-mobile-builder-back>Back</button><button type="button" class="button" data-mobile-builder-next>Next</button>';
    controls.appendChild(nav);

    const steps = [
      { actual: 1, title: 'Artwork & layout' },
      { actual: 1, title: 'Prepare artwork' },
      { actual: 2, title: 'Size & cut' },
      { actual: 3, title: 'Build the sheet' },
      { actual: 3, title: 'Sheet background' },
      { actual: 4, title: 'Add simple objects' },
      { actual: 4, title: 'Adjust selected item' },
      { actual: 5, title: 'Review & order' },
    ];
    let current = 1;

    const render = (scroll = false) => {
      const step = steps[current - 1];
      root.dataset.opsMobileWizardStep = String(current);
      if (stepOneTitle) setMobileText(stepOneTitle, current === 2 ? 'Prepare artwork' : 'Choose layout & add artwork');
      const builder = root.querySelector('sticker-sheet-builder');
      if (Number(builder?.dataset.currentStep || 1) !== step.actual) activateBuilderStep(root, step.actual);
      progress.querySelector('.ops-mobile-builder-progress__count').textContent = `Step ${current} of ${steps.length}`;
      progress.querySelector('.ops-mobile-builder-progress__title').textContent = step.title;
      progress.querySelector('.ops-mobile-builder-progress__dots').innerHTML = Array.from({ length: steps.length }, (_, index) => `<i class="${index + 1 <= current ? 'is-active' : ''}"></i>`).join('');
      nav.querySelector('[data-mobile-builder-back]').hidden = current === 1;
      const next = nav.querySelector('[data-mobile-builder-next]');
      next.hidden = current === steps.length;
      next.textContent = current === steps.length - 1 ? 'Next: review & order' : 'Next';
    };
    nav.querySelector('[data-mobile-builder-back]').addEventListener('click', () => { current = Math.max(1, current - 1); render(true); });
    nav.querySelector('[data-mobile-builder-next]').addEventListener('click', () => { current = Math.min(steps.length, current + 1); render(true); });
    render();
  }

  function initBuilder(scope = document) {
    scope.querySelectorAll('.ops-builder-section:not([data-ops-mobile-builder])').forEach((root) => {
      root.dataset.opsMobileBuilder = 'true';
      const productMode = isProductPage();
      const workspace = root.querySelector('.ops-builder__workspace');
      const controls = root.querySelector('.ops-builder__controls');
      const stage = workspace?.querySelector('.ops-builder-stage');
      const quality = workspace?.querySelector('[data-dpi-toolbar]');
      const stepOne = root.querySelector('[data-step-panel="1"]');
      if (!workspace || !controls || !stage || !stepOne) return;

      // Theme V20.3.27: the Sticker Sheet Maker now owns its own compact,
      // preview-led operator interface. Do not wrap it in the legacy eight-screen
      // mobile wizard or move its quality/canvas controls around.
      if (root.classList.contains('ops-builder-section--compact-editor')) {
        root.classList.add('ops-mobile-compact-builder');
        root.querySelectorAll('.ops-builder-tool-details[open], .ops-builder-details[open]').forEach((details) => { details.open = false; });
        return;
      }

      if (quality) {
        quality.classList.add('ops-mobile-builder-quality');
        move(quality, workspace, stage.nextSibling);
      }

      root.querySelectorAll('.ops-builder-tool-details[open]').forEach((details) => {
        details.dataset.opsMobileWasOpen = 'true';
        details.open = false;
      });

      if (productMode) {
        root.classList.add('ops-mobile-product-quick-builder');
        activateBuilderStep(root, 1);
        createBuilderProgress(root, workspace, true);
        createQuickBuilderActions(root);
        addEditorLink(workspace, root.dataset.editorUrl || '/pages/sticker-sheet-maker', 'Open the full Sticker Sheet Maker', 'Use advanced layout, cut and positioning tools');
        return;
      }

      root.classList.add('ops-mobile-full-builder');
      simplifyFullBuilder(root, workspace, controls);
    });
  }

  function initMobileNumberSteppers(scope = document) {
    const selector = [
      '.ops-mobile-full-builder input[type="number"]',
      '.ops-mobile-product-quick-builder input[type="number"]',
      '.ops-diecut-page[data-ops-mobile-diecut] input[type="number"]',
      '.ops-proof-page[data-ops-mobile-proof] input[type="number"]',
      '.ops-maker[data-ops-mobile-maker] input[type="number"]',
    ].join(',');

    scope.querySelectorAll(selector).forEach((input) => {
      // Die-cut already has its own purpose-built − / + quantity controls.
      if (input.closest('.ops-diecut-order-stepper')) return;
      if (input.dataset.opsMobileStepperReady === 'true' || input.disabled || input.readOnly) return;
      input.dataset.opsMobileStepperReady = 'true';

      const wrapper = addGenerated(document.createElement('span'));
      wrapper.className = 'ops-mobile-number-stepper';
      const minus = document.createElement('button');
      const plus = document.createElement('button');
      minus.type = plus.type = 'button';
      minus.className = 'ops-mobile-number-stepper__button';
      plus.className = 'ops-mobile-number-stepper__button';
      minus.textContent = '−';
      plus.textContent = '+';
      minus.setAttribute('aria-label', `Decrease ${input.getAttribute('aria-label') || input.name || 'value'}`);
      plus.setAttribute('aria-label', `Increase ${input.getAttribute('aria-label') || input.name || 'value'}`);

      input.parentNode.insertBefore(wrapper, input);
      wrapper.appendChild(minus);
      move(input, wrapper);
      wrapper.appendChild(plus);

      const nudge = (direction) => {
        const stepAttribute = input.getAttribute('step');
        const step = Number(stepAttribute && stepAttribute !== 'any' ? stepAttribute : 1) || 1;
        const minimum = input.min === '' ? -Infinity : Number(input.min);
        const maximum = input.max === '' ? Infinity : Number(input.max);
        const current = Number.isFinite(input.valueAsNumber) ? input.valueAsNumber : (Number(input.value) || 0);
        const decimalPlaces = String(step).includes('.') ? String(step).split('.')[1].length : 0;
        const next = Math.min(maximum, Math.max(minimum, current + (step * direction)));
        input.value = decimalPlaces ? next.toFixed(decimalPlaces) : String(Math.round(next));
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus({ preventScroll: true });
      };

      minus.addEventListener('click', () => nudge(-1));
      plus.addEventListener('click', () => nudge(1));
    });
  }

  function initMyArtwork(scope = document) {
    scope.querySelectorAll('.ops-library:not([data-ops-mobile-library])').forEach((root) => {
      root.dataset.opsMobileLibrary = 'true';
      const toolbar = root.querySelector('.ops-library__toolbar');
      const search = toolbar?.querySelector('.ops-library__search');
      if (toolbar && search) {
        const details = addGenerated(document.createElement('details'));
        details.className = 'ops-mobile-library-filters';
        details.innerHTML = '<summary><span>Filters & sort</span><b aria-hidden="true">＋</b></summary><div class="ops-mobile-library-filters__body"></div>';
        const body = details.querySelector('.ops-mobile-library-filters__body');
        [...toolbar.children].filter((child) => child !== search).forEach((child) => move(child, body));
        toolbar.appendChild(details);
        details.addEventListener('toggle', () => {
          details.querySelector('summary b').textContent = details.open ? '−' : '＋';
        });
      }

      const actions = root.querySelector('.ops-library__hero-actions');
      const secondary = actions ? [...actions.querySelectorAll('.ops-library__button:not(.is-primary)')] : [];
      if (actions && secondary.length) {
        const details = addGenerated(document.createElement('details'));
        details.className = 'ops-mobile-library-more';
        details.innerHTML = '<summary aria-label="More library actions">•••</summary><div></div>';
        const body = details.querySelector('div');
        secondary.forEach((button) => move(button, body));
        actions.appendChild(details);
      }
    });
  }

  function initMobile(scope = document) {
    initArtworkSources(scope);
    initPersistentMakerBars(scope);
    initUniversalCommandBars(scope);
    if (!media.matches) return;
    document.documentElement.classList.add('ops-mobile-interface-active');
    initProductUploads(scope);
    initProofLab(scope);
    initDieCut(scope);
    initProductMaker(scope);
    initBuilder(scope);
    initMyArtwork(scope);
    initMobileNumberSteppers(scope);
  }

  function restoreDesktop() {
    if (media.matches) return;
    document.documentElement.classList.remove('ops-mobile-interface-active');

    observers.forEach?.((observer) => observer.disconnect());

    document.querySelectorAll('[data-ops-mobile-proof]').forEach((root) => {
      delete root.dataset.opsMobileProof;
      delete root.dataset.opsMobileActive;
      root.querySelectorAll('[data-ops-mobile-panel]').forEach((card) => {
        card.classList.remove('is-ops-mobile-active');
        delete card.dataset.opsMobilePanel;
      });
    });

    document.querySelectorAll('[data-ops-mobile-diecut]').forEach((root) => {
      observers.get(root)?.disconnect();
      observers.delete(root);
      delete root.dataset.opsMobileDiecut;
      delete root.dataset.opsMobileActive;
      delete root.dataset.opsMobileDiecutStep;
      root.querySelectorAll('[data-ops-mobile-panel]').forEach((control) => {
        control.classList.remove('is-ops-mobile-active');
        delete control.dataset.opsMobilePanel;
      });
    });

    document.querySelectorAll('[data-ops-mobile-maker]').forEach((root) => {
      observers.get(root)?.disconnect();
      observers.delete(root);
      delete root.dataset.opsMobileMaker;
      delete root.dataset.opsMobileActive;
      delete root.dataset.opsMobileLayout;
      root.classList.remove('ops-mobile-maker-product', 'ops-mobile-maker-page', 'ops-mobile-maker-stacked');
      root.querySelectorAll('.ops-mobile-hide').forEach((node) => node.classList.remove('ops-mobile-hide'));
      root.querySelectorAll('.ops-mobile-control-source').forEach((node) => node.classList.remove('ops-mobile-control-source'));
      root.querySelectorAll('[data-ops-mobile-panel]').forEach((group) => {
        group.classList.remove('is-ops-mobile-active');
        delete group.dataset.opsMobilePanel;
      });
    });

    document.querySelectorAll('[data-ops-mobile-builder]').forEach((root) => {
      observers.get(root)?.disconnect();
      observers.delete(root);
      delete root.dataset.opsMobileBuilder;
      delete root.dataset.opsMobileBuilderStep;
      delete root.dataset.opsMobileWizardStep;
      root.classList.remove('ops-mobile-product-quick-builder', 'ops-mobile-full-builder');
      root.querySelectorAll('[data-ops-mobile-was-open="true"]').forEach((details) => {
        details.open = true;
        delete details.dataset.opsMobileWasOpen;
      });
      root.querySelectorAll('.ops-mobile-builder-quality').forEach((node) => node.classList.remove('ops-mobile-builder-quality'));
      root.querySelectorAll('.ops-mobile-advanced-only').forEach((node) => node.classList.remove('ops-mobile-advanced-only'));
      root.querySelectorAll('[data-ops-mobile-original-text]').forEach((node) => {
        node.textContent = node.dataset.opsMobileOriginalText;
        delete node.dataset.opsMobileOriginalText;
      });
    });

    document.querySelectorAll('[data-ops-mobile-library]').forEach((root) => delete root.dataset.opsMobileLibrary);
    document.querySelectorAll('[data-ops-mobile-upload]').forEach((root) => delete root.dataset.opsMobileUpload);
    document.querySelectorAll('[data-ops-mobile-source-ready]').forEach((input) => delete input.dataset.opsMobileSourceReady);
    // Source-trigger listeners are viewport-safe and stay bound across orientation changes.
    document.querySelectorAll('[data-ops-mobile-stepper-ready]').forEach((input) => delete input.dataset.opsMobileStepperReady);
    clearSourceUrls();
    sourceDialog = null;
    sourceInput = null;

    [...movedNodes].forEach(restoreNode);
    removeGenerated(document);
  }

  // Public entry point for makers that open the shared My Artwork source drawer.
  window.OPSArtworkSource = {
    open(input) {
      if (!input) return;
      bindArtworkSourceInput(input);
      openSourceDialog(input);
    }
  };

  const onMediaChange = () => {
    if (media.matches) initMobile(document);
    else {
      restoreDesktop();
      initArtworkSources(document);
      initPersistentMakerBars(document);
      initUniversalCommandBars(document);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initMobile(document), { once: true });
  else initMobile(document);

  media.addEventListener?.('change', onMediaChange);
  document.addEventListener('shopify:section:load', (event) => initMobile(event.target));
})();
